"use client";

import { forwardRef } from "react";
import shared from "./shared";
import styled from "styled-components";
import { textColorInverted, blue20, blue10, blue25, blue40, accentColorPrimary, setAppearanceConstantLocal } from "@churchofjesuschrist/eden-style-constants";

// TODO: The colors used by Primary buttons for hover/active do not shift by adjusting lightness like the Danger button does. If we want to use a single color with others that are derived, Unity/GVSG will need to adjust the colors used to reflect that direction.
import { jsx as _jsx } from "react/jsx-runtime";
const [buttonPrimaryHoverDef, buttonPrimaryHover] = setAppearanceConstantLocal("button-primary-hover", {
  light: blue25,
  dark: blue10,
  prefix: "eden"
});
const [buttonPrimaryActiveDef, buttonPrimaryActive] = setAppearanceConstantLocal("button-primary-active", {
  light: blue40,
  dark: blue20,
  prefix: "eden"
});
const Component = styled.button.withConfig({
  componentId: "sc-1lb7nf5-0"
})(["", ";", ";", ";background-color:", ";color:", ";@media (any-hover:hover){&:hover{background:", ";color:", ";}}&:active,&[aria-pressed=\"true\"]{background:", ";}&:is(a):visited:not(:active){color:", ";}"], shared, buttonPrimaryHoverDef, buttonPrimaryActiveDef, accentColorPrimary, textColorInverted, buttonPrimaryHover, textColorInverted, buttonPrimaryActive, textColorInverted);

/**
 * `Primary` buttons are used to indicate that an action is strongly recommended for a user to complete a specific task.
 * @param {object} props - The component props
 * @param {React.ReactNode} props.children - The button contents.
 * @param {boolean} [props.disabled] - If the button should be disabled.
 * @param {boolean} [props.fullWidth] - If the button should expand to the full width of its parent.
 * @param {string} [props.href] - The link that the button should navigate to on click.
 * @param {React.ElementType} [props.renderAs] - The HTML tag string or React component to render as.
 * @param {boolean} [props.small] - Whether to apply smaller styles.
 * @param {string} [props.type] - The [type of the button](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#Attributes) or the [type of the link](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#Attributes).
 * @param {boolean} [props."aria-pressed"] - When present, the button can act as a "toggle" button. The value indicates whether it is toggled on or not. Control this with your application state.
 * @param {React.Ref} ref - Ref forwarded to the underlying element.
 * @returns {React.ReactElement} The Primary component
 */
const PrimaryComponent = ({
  href,
  disabled,
  renderAs = href && !disabled ? "a" : "button",
  type = renderAs === "button" ? "button" : undefined,
  small,
  fullWidth,
  ...props
}, ref) => /*#__PURE__*/_jsx(Component, {
  ...props,
  ref: ref,
  type: type,
  href: href,
  disabled: disabled,
  $fullWidth: fullWidth,
  $small: small,
  as: renderAs,
  children: props.children
});
const Primary = /*#__PURE__*/forwardRef(PrimaryComponent);
Primary.displayName = "Primary";
export default Primary;